/**
 * Панель фильтров — единственный источник того, что показывают карточки.
 *
 * Чат пишет ровно в эти же поля, поэтому после реплики видно, что именно
 * изменилось: правка чипа и фраза человека — одно и то же действие.
 */

import { suggestPeople } from "../api/client";
import type { Granularity, MetricKey, PeriodSpec } from "../api/types";
import { DEFAULT_STATE, GRANULARITY_LABELS, METRIC_LABELS, ROLE_LABELS } from "../state/filters";
import { useStore } from "../state/store";
import { ProgramPicker } from "./ProgramPicker";
import { Suggest } from "./Suggest";

// Годы приходят из /me/ и считаются по базе. Зашитый здесь список
// [2026, 2025, 2024, 2023] предлагал два года, за которые выпусков нет вовсе:
// отсечка каталога стоит на 2025-01-01, и выбор 2023-го давал пустую карточку,
// неотличимую от поломки. Пока /me/ не пришёл, показываем один текущий год —
// угадывать глубину архива на клиенте нечем.
const FALLBACK_YEARS = [new Date().getFullYear()];
// Роль по умолчанию берётся из состояния, а не пишется здесь второй раз:
// разъехавшись, эти два места дали бы кнопку «вернуть ведущего», которая
// возвращает не то, что ставит сброс фильтров.
const DEFAULT_ROLE = DEFAULT_STATE.role;
const QUARTERS = [1, 2, 3, 4];
const MONTHS = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

export function FilterBar() {
  const { state, patch, reset, me } = useStore();
  // Доход скрыт и на сервере тоже: селектор без него — удобство, а не защита.
  const metrics = (Object.keys(METRIC_LABELS) as MetricKey[]).filter(
    (metric) => metric !== "revenue" || me?.can_see_revenue,
  );

  return (
    <div className="filters">
      <PeriodPicker
        value={state.period}
        years={me?.years?.length ? me.years : FALLBACK_YEARS}
        onChange={(period) => patch({ period })}
      />

      <Suggest
        label="Человек"
        value={state.person}
        onChange={(person) => patch({ person })}
        search={(query) => suggestPeople(query).then((data) => data.results.map((row) => row.name))}
      />

      <Suggest
        label="Второй человек"
        value={state.person_b}
        placeholder="для сравнения"
        onChange={(person_b) => patch({ person_b })}
        search={(query) => suggestPeople(query).then((data) => data.results.map((row) => row.name))}
      />

      <ProgramPicker value={state.program} onChange={(program) => patch({ program })} />

      {/* Роль выбирать нечем: для MVP нужен только ведущий, и селектор с
          четырьмя значениями предлагал разрезы, которых заказчик не просил.
          Но роль осталась в состоянии, и чат её меняет репликой — значит
          не-дефолтное значение обязано быть на экране. Скрытый фильтр,
          отсекающий часть выпусков, и есть тот самый тихий отказ: числа
          правдоподобные, а основание невидимо. */}
      {state.role !== DEFAULT_ROLE && (
        <div className="field">
          <label className="field__label">Роль</label>
          <div className="field__row">
            <span className="chip chip--accrued">{ROLE_LABELS[state.role]}</span>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => patch({ role: DEFAULT_ROLE })}
            >
              вернуть ведущего
            </button>
          </div>
        </div>
      )}

      <div className="field">
        <label className="field__label">Метрика</label>
        <select
          className="input"
          value={state.metric}
          onChange={(event) => patch({ metric: event.target.value as MetricKey })}
        >
          {metrics.map((metric) => (
            <option key={metric} value={metric}>
              {METRIC_LABELS[metric]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field__label">Шаг графика</label>
        <select
          className="input"
          value={state.granularity ?? ""}
          onChange={(event) =>
            patch({ granularity: (event.target.value || null) as Granularity | null })
          }
        >
          <option value="">по длине периода</option>
          {(Object.keys(GRANULARITY_LABELS) as Granularity[]).map((step) => (
            <option key={step} value={step}>
              {GRANULARITY_LABELS[step]}
            </option>
          ))}
        </select>
      </div>

      <button type="button" className="button button--ghost filters__reset" onClick={reset}>
        Сбросить
      </button>
    </div>
  );
}

function PeriodPicker({
  value,
  years,
  onChange,
}: {
  value: PeriodSpec;
  years: number[];
  onChange: (period: PeriodSpec) => void;
}) {
  const year = value.year ?? years[0] ?? new Date().getFullYear();
  // Выбранный год показывается всегда, даже если его нет в списке: по ссылке
  // с ?period=year:2024 состояние сохраняет 2024, и селектор обязан показывать
  // именно его. Иначе на экране один год, а считается другой — карточка при
  // этом честно скажет, что данных нет.
  const options = years.includes(year) ? years : [...years, year].sort((a, b) => b - a);
  return (
    <div className="field field--period">
      <label className="field__label">Период</label>
      <div className="field__row">
        <select
          className="input"
          value={value.kind}
          onChange={(event) =>
            onChange(nextPeriod(event.target.value as PeriodSpec["kind"], value, year))
          }
        >
          <option value="all">за всё время</option>
          <option value="year">год</option>
          <option value="quarter">квартал</option>
          <option value="month">месяц</option>
          <option value="last_days">последние дни</option>
        </select>

        {(value.kind === "year" || value.kind === "quarter" || value.kind === "month") && (
          <select
            className="input"
            value={year}
            onChange={(event) => onChange({ ...value, year: Number(event.target.value) })}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )}

        {value.kind === "quarter" && (
          <select
            className="input"
            value={value.index ?? 1}
            onChange={(event) => onChange({ ...value, index: Number(event.target.value) })}
          >
            {QUARTERS.map((quarter) => (
              <option key={quarter} value={quarter}>
                {quarter} квартал
              </option>
            ))}
          </select>
        )}

        {value.kind === "month" && (
          <select
            className="input"
            value={value.index ?? 1}
            onChange={(event) => onChange({ ...value, index: Number(event.target.value) })}
          >
            {MONTHS.map((month, position) => (
              <option key={month} value={position + 1}>
                {month}
              </option>
            ))}
          </select>
        )}

        {value.kind === "last_days" && (
          <select
            className="input"
            value={value.days ?? 30}
            onChange={(event) => onChange({ ...value, days: Number(event.target.value) })}
          >
            {[7, 30, 90, 180, 365].map((days) => (
              <option key={days} value={days}>
                {days} дней
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

function nextPeriod(kind: PeriodSpec["kind"], current: PeriodSpec, fallbackYear: number): PeriodSpec {
  // fallbackYear приходит из уже вычисленного списка, а не из зашитой
  // константы: при переключении «за всё время» → «год» подставляется свежий
  // год, за который данные есть.
  const year = current.year ?? fallbackYear;
  switch (kind) {
    case "year":
      return { kind, year };
    case "quarter":
      return { kind, year, index: current.index && current.index <= 4 ? current.index : 1 };
    case "month":
      return { kind, year, index: current.index && current.index <= 12 ? current.index : 1 };
    case "last_days":
      return { kind, days: current.days ?? 30 };
    default:
      return { kind: "all" };
  }
}
